import { Bug, MessageSquareText, X } from "lucide-react";
import { useEffect, useRef } from "react";
import {
  type SupportRequestContext,
  SupportRequestForm,
  type SupportRequestKind,
} from "./SupportRequestForm";

interface SupportDialogProps {
  kind: Exclude<SupportRequestKind, "support">;
  context: SupportRequestContext;
  onClose: () => void;
}

export function SupportDialog({ kind, context, onClose }: SupportDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [onClose]);

  const isIssue = kind === "issue";
  return (
    <div className="modal-backdrop support-dialog-backdrop">
      <button
        className="modal-dismiss"
        type="button"
        aria-label="Close support form"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className="support-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-dialog-title"
        tabIndex={-1}
      >
        <header>
          <span className={`support-dialog-icon ${isIssue ? "issue" : "feedback"}`}>
            {isIssue ? <Bug /> : <MessageSquareText />}
          </span>
          <div>
            <span className="eyebrow">{isIssue ? "REPORT ISSUE" : "SEND FEEDBACK"}</span>
            <h2 id="support-dialog-title">
              {isIssue ? "Tell us what went wrong." : "Help shape Topzle."}
            </h2>
          </div>
          <button className="icon-button" type="button" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </header>
        <p>
          {isIssue
            ? "Include what you expected, what happened, and the last action you took. We’ll include technical context automatically."
            : "Share an idea, a game request, or anything that would make playing together better."}
        </p>
        <SupportRequestForm
          initialKind={kind}
          context={context}
          footer={
            <button className="button button-ghost" type="button" onClick={onClose}>
              Cancel
            </button>
          }
        />
      </div>
    </div>
  );
}
